import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Fiber from "effect/Fiber";
import * as Scope from "effect/Scope";
import * as Semaphore from "effect/Semaphore";
import { freezeMetadata, ValidationError } from "@linempo/kernel";
import { animationGeometryKey, animationScene, assetByHash, sceneReferences, annotationScenes, applyEdit, makeProject, subtitleSelectionRange } from "@linempo/project-domain";
import { Cancelled } from "@linempo/render-domain";
import { EditorCapabilities } from "@linempo/application";
import { asRenderError, checkedProject, checkedSettings, idleJob, initialState, workerError } from "./model.js";
import { makeHistory } from "./history.js";
import { makePolicies } from "./policies.js";
import { reconcileSelection, selectCueRange } from "./selection.js";
/** Owns metadata and policy fibers. Native resources remain in the capability's enclosing Scope. */
export function makeSession(options) {
    return Effect.gen(function* () {
        const capabilities = yield* EditorCapabilities;
        const owner = yield* Effect.scope;
        const context = yield* Effect.context();
        const tasks = Scope.makeUnsafe();
        let projectTasks = Scope.forkUnsafe(tasks);
        const prepareLock = Semaphore.makeUnsafe(1);
        const saveLock = Semaphore.makeUnsafe(1);
        const importLock = Semaphore.makeUnsafe(1);
        const history = makeHistory();
        const listeners = new Set();
        let state = initialState();
        let generation = 0;
        let prepareSequence = 0;
        let seekSequence = 0;
        let manualPrepare = null;
        let activePrepare = null;
        let generationSequence = 0;
        let generationCancelled = false;
        let generationFiber = null;
        let cancelGenerationBarrier = null;
        const durations = [];
        let exportPreparation = null;
        let exportSequence = 0;
        let activeExport = null;
        const presenters = new Set();
        let disposed = false;
        let disposal = null;
        const snapshot = () => state;
        const update = (change) => {
            if (disposed)
                return;
            state = freezeMetadata(change(state));
            for (const listener of listeners) {
                try {
                    listener();
                }
                catch { /* Observers cannot abort a transaction. */ }
            }
        };
        const current = (epoch) => !disposed && epoch === generation;
        const cancelled = () => new Cancelled({ jobId: "editor-session" });
        const guardEpoch = (epoch) => { if (!current(epoch))
            throw cancelled(); };
        const guardOpen = () => { if (disposed)
            throw workerError("unavailable", "编辑会话已关闭。"); };
        const guardEditable = () => {
            guardOpen();
            if (state.export.locked)
                throw workerError("busy", "导出期间无法编辑，请先取消导出。");
            if (state.workspace.switching)
                throw workerError("busy", "工程正在载入。");
        };
        const project = () => { if (!state.project)
            throw workerError("protocol", "请先创建或载入工程。"); return state.project; };
        const attempt = (body) => Effect.try({ try: body, catch: asRenderError });
        const report = (error, epoch = generation) => { if (current(epoch) && error._tag !== "Cancelled")
            update((s) => ({ ...s, error })); };
        const start = (effect, scope = tasks) => Effect.runSyncWith(context)(Effect.forkIn(effect, scope, { startImmediately: true }));
        const observe = (fiber, epoch) => new Promise((resolve, reject) => fiber.addObserver((exit) => {
            if (Exit.isSuccess(exit))
                resolve(exit.value);
            else {
                const error = Cause.hasInterruptsOnly(exit.cause) ? cancelled() : asRenderError(Cause.squash(exit.cause));
                report(error, epoch);
                reject(error);
            }
        }));
        const run = (effect, epoch = generation, scope = tasks) => {
            if (disposed)
                return Promise.reject(workerError("unavailable", "编辑会话已关闭。"));
            return observe(start(effect, scope), epoch);
        };
        const background = (effect, epoch) => Effect.asVoid(effect.pipe(Effect.catch((error) => Effect.sync(() => report(error, epoch)))));
        const policies = makePolicies((effect) => start(effect));
        const workspace = (effect, epoch, importing = false) => Effect.acquireUseRelease(Effect.sync(() => { if (current(epoch))
            update((s) => ({ ...s, workspace: { ...s.workspace, pending: s.workspace.pending + 1, importing: importing || s.workspace.importing } })); }), () => effect, () => Effect.sync(() => { if (current(epoch))
            update((s) => ({ ...s, workspace: { ...s.workspace, pending: Math.max(0, s.workspace.pending - 1), importing: importing ? false : s.workspace.importing } })); }));
        const generationBusy = () => state.generation.status === "running" || state.generation.status === "cancelling";
        const sameTarget = (a, b) => a._tag === b._tag && (a._tag !== "animation" || b._tag === "animation" && a.animationId === b.animationId);
        const targetDuration = (content, target = state.preview.target) => target._tag === "timeline" ? content.durationMs : animationScene(content, target.animationId)?.endMs ?? 0;
        const inUse = (content) => [...new Set(sceneReferences(content).map((scene) => scene.animationId))];
        const missingFor = (content, ids) => ids.filter((id) => !state.animations.inspections.some((item) => item.animationId === id && item.status === "ready" && item.inputKey === animationGeometryKey(content, id)));
        const inspect = (epoch) => Effect.gen(function* () {
            const content = yield* attempt(() => { guardEpoch(epoch); return project(); });
            update((s) => ({ ...s, animations: { ...s.animations, checking: true } }));
            const inspections = yield* capabilities.inspectAnimations(content, content.animations.map((animation) => animation.id));
            if (!current(epoch) || state.project?.revision !== content.revision)
                return false;
            update((s) => ({ ...s, animations: { inspections, checking: false, missingInUse: inUse(content).filter((id) => !inspections.some((item) => item.animationId === id && item.status === "ready" && item.inputKey === animationGeometryKey(content, id))) } }));
            return true;
        }).pipe(Effect.ensuring(Effect.sync(() => { if (current(epoch))
            update((s) => ({ ...s, animations: { ...s.animations, checking: false } })); })));
        const planIsCurrent = (plan) => !!plan && !!state.project && plan.projectId === state.project.id && plan.projectRevision === state.project.revision && sameTarget(plan.target ?? { _tag: "timeline" }, state.preview.target);
        const acceptsFrame = (frame) => {
            const plan = state.prepare.plan;
            return !disposed && planIsCurrent(plan) && frame.projectId === plan.projectId && frame.projectRevision === plan.projectRevision
                && frame.planId === plan.planId && frame.jobId === plan.jobId && frame.timeMs === state.preview.requestedTimeMs
                && (!state.preview.frame || state.preview.frame.planId !== frame.planId || frame.requestSequence >= state.preview.frame.requestSequence);
        };
        const seek = (timeMs, target) => Effect.gen(function* () {
            const request = yield* attempt(() => {
                guardOpen();
                const content = project();
                if (!Number.isFinite(timeMs) || timeMs < 0 || timeMs > targetDuration(content))
                    throw new ValidationError({ path: "preview.time", message: "播放头须位于工程时间范围内。" });
                const plan = state.prepare.plan;
                const sequence = ++seekSequence;
                update((s) => ({ ...s, preview: { ...s.preview, requestedTimeMs: timeMs, timelineTimeMs: s.preview.target._tag === "timeline" ? timeMs : s.preview.timelineTimeMs, seeking: planIsCurrent(plan) } }));
                return { plan, sequence, epoch: generation };
            });
            if (!planIsCurrent(request.plan))
                return false;
            return yield* capabilities.seek(request.plan, timeMs, target).pipe(Effect.map((accepted) => current(request.epoch) && request.sequence === seekSequence && planIsCurrent(request.plan) && accepted), Effect.catch((error) => !current(request.epoch) || request.sequence !== seekSequence || !planIsCurrent(request.plan) ? Effect.succeed(false) : Effect.fail(error)), Effect.ensuring(Effect.sync(() => { if (current(request.epoch) && request.sequence === seekSequence)
                update((s) => ({ ...s, preview: { ...s.preview, seeking: false } })); })));
        });
        const save = (epoch) => saveLock.withPermit(Effect.gen(function* () {
            const input = yield* attempt(() => {
                guardEpoch(epoch);
                const content = project();
                if (content.revision === state.savedRevision)
                    return null;
                update((s) => ({ ...s, save: { status: "saving", error: null } }));
                return { content, expected: state.savedRevision };
            });
            if (!input)
                return;
            // A pending switch retains this project and waits for saveLock before publishing
            // its replacement. Preserve the CAS result if that switch later fails.
            const ownsContent = () => current(epoch) || (!disposed && state.workspace.switching && state.project?.id === input.content.id);
            yield* capabilities.saveProject(input.content, input.expected).pipe(Effect.tapError((error) => Effect.sync(() => {
                if (ownsContent())
                    update((s) => ({ ...s, save: { status: "failed", error } }));
            })));
            if (!ownsContent())
                return;
            update((s) => ({ ...s, savedRevision: input.content.revision, save: { status: s.project?.revision === input.content.revision ? "saved" : "pending", error: null } }));
        }));
        const queueSave = (epoch) => { if (current(epoch))
            start(background(save(epoch), epoch)); };
        const prepare = (epoch, sequence, forExport = false) => prepareLock.withPermit(Effect.gen(function* () {
            yield* attempt(() => { guardEpoch(epoch); if (sequence !== prepareSequence)
                throw cancelled(); if (!forExport)
                guardEditable(); if (generationBusy())
                throw workerError("busy", "请等待生成完成，或先取消生成。"); });
            yield* capabilities.cancel("prepare");
            const input = yield* attempt(() => {
                guardEpoch(epoch);
                if (sequence !== prepareSequence)
                    throw cancelled();
                const content = project();
                if (targetDuration(content) <= 0)
                    throw new ValidationError({ path: "preview.empty", message: "请先导入字幕或添加镜头。" });
                activePrepare = { generation: epoch, sequence, revision: content.revision };
                update((s) => ({ ...s, prepare: { ...idleJob(), status: "running", plan: null }, preview: { ...s.preview, updating: true }, error: null }));
                return content;
            });
            if (!(yield* inspect(epoch)))
                return yield* Effect.fail(cancelled());
            yield* attempt(() => {
                guardEpoch(epoch);
                if (sequence !== prepareSequence || state.project?.revision !== input.revision)
                    throw cancelled();
                const ids = state.preview.target._tag === "timeline" ? inUse(input) : [state.preview.target.animationId];
                const missing = missingFor(input, ids);
                if (missing.length)
                    throw new ValidationError({ path: "animation.missing", message: `请先生成动画：${missing.map((id) => input.assets.find((asset) => asset.id === input.animations.find((animation) => animation.id === id)?.assetId)?.name ?? id).join("、")}` });
            });
            const target = state.preview.target;
            const prepared = yield* capabilities.prepare(input, target);
            yield* attempt(() => {
                guardEpoch(epoch);
                if (sequence !== prepareSequence || input.revision !== state.project?.revision)
                    throw cancelled();
                if (!planIsCurrent(prepared) || prepared.durationMs !== targetDuration(input, target))
                    throw workerError("protocol", "预览计划与当前工程不一致。");
                update((s) => ({ ...s, prepare: { ...s.prepare, status: "ready", jobId: prepared.jobId, plan: prepared, error: null }, preview: { ...s.preview, updating: false } }));
            });
            yield* seek(Math.min(state.preview.requestedTimeMs, prepared.durationMs));
            return prepared;
        }).pipe(Effect.catch((error) => !current(epoch) || sequence !== prepareSequence ? Effect.fail(cancelled()) : Effect.fail(error)), Effect.tapError((error) => Effect.sync(() => {
            if (current(epoch) && sequence === prepareSequence)
                update((s) => ({ ...s, prepare: { ...s.prepare, status: error._tag === "Cancelled" ? "cancelled" : "failed", plan: null, error: error._tag === "Cancelled" ? null : error }, preview: { ...s.preview, updating: false, seeking: false } }));
        })), Effect.onInterrupt(() => capabilities.cancel("prepare").pipe(Effect.ignore)), Effect.ensuring(Effect.sync(() => { if (activePrepare?.sequence === sequence)
            activePrepare = null; }))));
        const schedule = (persist) => {
            const epoch = generation;
            const sequence = ++prepareSequence;
            activePrepare = null;
            manualPrepare?.interruptUnsafe();
            if (state.project && !generationBusy())
                policies.prepare(background(Effect.gen(function* () {
                    if (!(yield* inspect(epoch)) || sequence !== prepareSequence || generationBusy())
                        return;
                    const content = project();
                    const ids = state.preview.target._tag === "timeline" ? inUse(content) : [state.preview.target.animationId];
                    if (targetDuration(content) > 0 && !missingFor(content, ids).length)
                        yield* prepare(epoch, sequence);
                    else
                        update((s) => ({ ...s, preview: { ...s.preview, updating: false } }));
                }), epoch));
            else
                policies.stopPrepare();
            if (persist)
                policies.save(() => queueSave(epoch));
        };
        const publishEdit = (next) => {
            ++seekSequence;
            const target = state.preview.target._tag === "animation" && next.animations.some((animation) => animation.id === state.preview.target.animationId) ? state.preview.target : { _tag: "timeline" };
            update((s) => ({ ...s, project: next, history: history.view(), selection: reconcileSelection(next, s.selection), prepare: { ...idleJob(), plan: null }, export: { ...s.export, error: null },
                animations: { ...s.animations, inspections: s.animations.inspections.filter((item) => next.animations.some((animation) => animation.id === item.animationId) && item.inputKey === animationGeometryKey(next, item.animationId)), missingInUse: missingFor(next, inUse(next)) },
                preview: { ...s.preview, target, timelineTimeMs: Math.min(s.preview.timelineTimeMs, next.durationMs), requestedTimeMs: Math.min(target._tag === s.preview.target._tag ? s.preview.requestedTimeMs : s.preview.timelineTimeMs, targetDuration(next, target)), seeking: false, playing: false, updating: next.durationMs > 0 }, save: { status: "pending", error: null }, error: null }));
            schedule(true);
        };
        const commit = (input) => {
            guardEditable();
            const before = project();
            const next = checkedProject(applyEdit(before, input));
            history.record(before);
            publishEdit(next);
            return next;
        };
        const restore = (direction) => {
            guardEditable();
            const before = project();
            const target = history.peek(direction);
            if (!target)
                return before;
            const next = checkedProject(applyEdit(before, { _tag: "RestoreContent", project: target }));
            history.accept(direction, before);
            publishEdit(next);
            return next;
        };
        const beginSwitch = () => {
            guardOpen();
            if (state.export.locked)
                throw workerError("busy", "导出期间无法切换工程，请先取消导出。");
            const interruptGeneration = generationBusy();
            ++generation;
            ++generationSequence;
            generationCancelled = true;
            ++prepareSequence;
            ++seekSequence;
            activePrepare = null;
            activeExport = null;
            policies.stop();
            const previous = projectTasks;
            projectTasks = Scope.forkUnsafe(tasks);
            update((s) => ({ ...s, prepare: { ...idleJob(), plan: null }, preview: { ...s.preview, playing: false, seeking: false, updating: false },
                generation: interruptGeneration ? { ...s.generation, status: "cancelled", finishedAtMs: Date.now(), activeAnimationId: null, estimatedRemainingMs: null, items: s.generation.items.map((item) => item.status === "running" || item.status === "queued" ? { ...item, status: "cancelled" } : item) } : s.generation, workspace: { pending: 0, importing: false, switching: true }, error: null }));
            return { epoch: generation, previous, scope: projectTasks };
        };
        const switchProject = (load, loaded) => {
            let switching;
            try {
                switching = beginSwitch();
            }
            catch (error) {
                const typed = asRenderError(error);
                report(typed);
                return Promise.reject(typed);
            }
            const { epoch, previous, scope } = switching;
            return run(Effect.gen(function* () {
                yield* Scope.close(previous, Exit.void);
                yield* prepareLock.withPermit(capabilities.cancel("all"));
                yield* attempt(() => guardEpoch(epoch));
                // An already dispatched write settles before reading the same persisted identity.
                const content = yield* saveLock.withPermit(load());
                yield* attempt(() => {
                    guardEpoch(epoch);
                    if (content)
                        checkedProject(content);
                    history.clear();
                    update((s) => ({ ...initialState(), settings: s.settings, zoom: s.zoom, project: content, savedRevision: loaded ? content?.revision ?? null : null,
                        save: { status: loaded && content ? "saved" : content ? "pending" : "idle", error: null }, preview: { ...initialState().preview, updating: !!content && content.durationMs > 0 } }));
                    schedule(!loaded && !!content);
                });
                return content;
            }).pipe(Effect.ensuring(Effect.sync(() => {
                if (!current(epoch) || !state.workspace.switching)
                    return;
                update((s) => ({ ...s, workspace: { ...s.workspace, switching: false }, preview: { ...s.preview, updating: !!s.project && s.project.durationMs > 0 } }));
                schedule(!!state.project && state.project.revision !== state.savedRevision);
            }))), epoch, scope);
        };
        const startPreview = (target) => {
            try {
                guardEditable();
                const content = project();
                if (generationBusy())
                    throw workerError("busy", "请等待生成完成，或先取消生成。");
                if (target._tag === "animation" && !content.animations.some((animation) => animation.id === target.animationId))
                    throw new ValidationError({ path: "edit.animation", message: "动画不存在。" });
                policies.stopPrepare();
                manualPrepare?.interruptUnsafe();
                ++seekSequence;
                update((s) => ({ ...s, prepare: { ...idleJob(), plan: null }, preview: { ...s.preview, target, requestedTimeMs: target._tag === "timeline" ? s.preview.timelineTimeMs : 0, playing: false, seeking: false, updating: true } }));
                const sequence = ++prepareSequence;
                manualPrepare = start(prepare(generation, sequence), projectTasks);
                return observe(manualPrepare, generation);
            }
            catch (error) {
                const typed = asRenderError(error);
                report(typed);
                return Promise.reject(typed);
            }
        };
        const updateItem = (id, change) => update((s) => {
            const items = s.generation.items.map((item) => item.animationId === id ? { ...item, ...change } : item);
            const remaining = items.filter((item) => item.status === "queued" || item.status === "running").length;
            const average = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : null;
            return { ...s, generation: { ...s.generation, items, completed: items.filter((item) => item.status === "completed").length, estimatedRemainingMs: average === null ? null : average * remaining } };
        });
        const generateAnimations = (requested) => {
            let ids;
            const epoch = generation;
            try {
                guardEditable();
                if (generationBusy())
                    throw workerError("busy", "生成队列正在运行。");
                const content = project();
                ids = [...new Set(requested ?? content.animations.map((animation) => animation.id))];
                if (ids.some((id) => !content.animations.some((animation) => animation.id === id)))
                    throw new ValidationError({ path: "edit.animation", message: "动画不存在。" });
            }
            catch (error) {
                const typed = asRenderError(error);
                report(typed);
                return Promise.reject(typed);
            }
            if (!ids.length)
                return Promise.resolve();
            const sequence = ++generationSequence;
            generationCancelled = false;
            policies.stopPrepare();
            manualPrepare?.interruptUnsafe();
            ++prepareSequence;
            activePrepare = null;
            ++seekSequence;
            update((s) => ({ ...s, prepare: { ...idleJob(), plan: null }, preview: { ...s.preview, playing: false, seeking: false, updating: false }, generation: { status: "running", startedAtMs: Date.now(), finishedAtMs: null, items: ids.map((animationId) => ({ animationId, status: "queued", progress: null, result: null, error: null })), activeAnimationId: null, completed: 0, total: ids.length, estimatedRemainingMs: null }, error: null }));
            const work = prepareLock.withPermit(Effect.gen(function* () {
                yield* capabilities.cancel("prepare");
                for (const id of ids) {
                    if (!current(epoch) || sequence !== generationSequence || generationCancelled)
                        break;
                    const content = project();
                    updateItem(id, { status: "running" });
                    update((s) => ({ ...s, generation: { ...s.generation, activeAnimationId: id } }));
                    const outcome = yield* Effect.exit(capabilities.generate(content, id));
                    if (!current(epoch) || sequence !== generationSequence || generationCancelled)
                        break;
                    if (Exit.isSuccess(outcome)) {
                        const result = outcome.value;
                        if (result.animationId !== id || !state.project?.animations.some((animation) => animation.id === id) || result.inputKey !== animationGeometryKey(project(), id)) {
                            updateItem(id, { status: "failed", error: new ValidationError({ path: "animation.changed", message: "生成期间动画内容已改变，请重新生成。" }) });
                        }
                        else {
                            if (!result.reused) {
                                durations.push(result.elapsedMs);
                                if (durations.length > 20)
                                    durations.shift();
                            }
                            updateItem(id, { status: "completed", result });
                        }
                    }
                    else {
                        const error = Cause.hasInterruptsOnly(outcome.cause) ? cancelled() : asRenderError(Cause.squash(outcome.cause));
                        updateItem(id, { status: error._tag === "Cancelled" ? "cancelled" : "failed", error: error._tag === "Cancelled" ? null : error });
                    }
                }
            })).pipe(Effect.tapError((error) => Effect.sync(() => {
                if (!current(epoch) || sequence !== generationSequence)
                    return;
                for (const item of state.generation.items)
                    if (item.status === "queued" || item.status === "running")
                        updateItem(item.animationId, { status: "failed", error });
            })), Effect.ensuring(Effect.sync(() => {
                if (!current(epoch) || sequence !== generationSequence)
                    return;
                update((s) => ({ ...s, generation: { ...s.generation, status: "completed", finishedAtMs: Date.now(), activeAnimationId: null } }));
                schedule(false);
            })));
            generationFiber = start(work, projectTasks);
            return observe(generationFiber, epoch);
        };
        const cancelGeneration = () => {
            if (cancelGenerationBarrier)
                return cancelGenerationBarrier;
            if (!generationBusy())
                return Promise.resolve();
            const epoch = generation;
            const sequence = ++generationSequence;
            generationCancelled = true;
            update((s) => ({ ...s, generation: { ...s.generation, status: "cancelling" } }));
            generationFiber?.interruptUnsafe();
            cancelGenerationBarrier = run(prepareLock.withPermit(capabilities.cancel("generate")).pipe(Effect.tap(() => Effect.sync(() => {
                if (!current(epoch) || sequence !== generationSequence)
                    return;
                update((s) => ({ ...s, generation: { ...s.generation, status: "cancelled", finishedAtMs: Date.now(), activeAnimationId: null, estimatedRemainingMs: null, items: s.generation.items.map((item) => item.status === "running" || item.status === "queued" ? { ...item, status: "cancelled" } : item) } }));
                schedule(false);
            }))), epoch).finally(() => { cancelGenerationBarrier = null; });
            return cancelGenerationBarrier;
        };
        const receive = (event) => {
            if (disposed)
                return;
            if (event.kind === "frame") {
                if (acceptsFrame(event.frame))
                    update((s) => ({ ...s, preview: { ...s.preview, frame: event.frame, seeking: false } }));
                return;
            }
            if (event.kind === "generate") {
                if (state.generation.status !== "running" || event.animationId !== state.generation.activeAnimationId)
                    return;
                if (event.state.status === "running")
                    update((s) => ({ ...s, generation: { ...s.generation, items: s.generation.items.map((item) => item.animationId === event.animationId && item.status === "running" ? { ...item, progress: event.state.progress } : item) } }));
                return;
            }
            if (event.kind === "prepare") {
                const retained = state.prepare.plan;
                if (planIsCurrent(retained) && event.state.jobId === retained.jobId && (event.state.status === "failed" || event.state.status === "cancelled")) {
                    update((s) => ({ ...s, prepare: { ...event.state, plan: null }, preview: { ...s.preview, playing: false, seeking: false, updating: false }, error: event.state.error ?? s.error }));
                    return;
                }
                const active = activePrepare;
                if (!active || !current(active.generation) || active.sequence !== prepareSequence || active.revision !== state.project?.revision || (event.plan && !planIsCurrent(event.plan)))
                    return;
                // Only the matching operation result may publish completion.
                if (event.state.status === "running")
                    update((s) => ({ ...s, prepare: { ...s.prepare, jobId: event.state.jobId, progress: event.state.progress } }));
            }
            if (event.kind === "export") {
                const active = activeExport;
                if (!active || !current(active.generation) || active.revision !== state.project?.revision || !state.export.locked || (event.artifact && event.artifact.projectRevision !== active.revision))
                    return;
                if (event.state.status === "running" || event.state.status === "cancelling")
                    update((s) => ({ ...s, export: { ...s.export, jobId: event.state.jobId, progress: event.state.progress, status: event.state.status } }));
            }
            // Unscoped failure events have no project/revision; the owning operation reports errors.
        };
        const unsubscribe = capabilities.subscribe(receive);
        const commands = {
            newProject: (name) => switchProject(() => attempt(() => makeProject(options.createId(), name)), false),
            loadProject: (id) => switchProject(() => capabilities.loadProject(id).pipe(Effect.flatMap((value) => attempt(() => value === null ? null : checkedProject(value)))), true),
            saveProject: () => { policies.stopSave(); return run(save(generation)); },
            commit: (edit) => run(attempt(() => commit(edit))), undo: () => run(attempt(() => restore("undo"))), redo: () => run(attempt(() => restore("redo"))),
            importAsset: (native, assetId) => {
                const epoch = generation;
                return run(Effect.andThen(attempt(() => { guardEpoch(epoch); guardEditable(); project(); }), importLock.withPermit(Effect.gen(function* () {
                    yield* attempt(() => { guardEpoch(epoch); guardEditable(); project(); });
                    const asset = yield* workspace(capabilities.importAsset(native, assetId ?? options.createId()), epoch, true);
                    return yield* attempt(() => { guardEpoch(epoch); const updated = commit({ _tag: "AddAssets", assets: [asset] }); const actual = assetByHash(updated, asset.hash); update((s) => ({ ...s, selection: { ...s.selection, assetId: actual.id, assetIds: [actual.id], activeRegion: "library" } })); return actual; });
                }))), epoch, projectTasks);
            },
            importSubtitle: (source, name, mode) => {
                const epoch = generation;
                return run(Effect.andThen(attempt(() => { guardEpoch(epoch); guardEditable(); project(); }), importLock.withPermit(Effect.gen(function* () {
                    yield* attempt(() => { guardEpoch(epoch); guardEditable(); project(); });
                    const result = yield* workspace(capabilities.parseSubtitle(source), epoch, true);
                    return yield* attempt(() => {
                        guardEpoch(epoch);
                        const track = { _tag: "subtitles", id: options.createId(), cues: result.cues.map((cue) => ({ ...cue, id: options.createId() })), metadata: { sourceHash: result.sourceHash, sourceName: name } };
                        commit({ _tag: "ImportSubtitles", mode, track });
                        return track;
                    });
                }))), epoch, projectTasks);
            },
            readAsset: (asset) => run(capabilities.readAsset(asset)), readThumbnail: (asset) => run(capabilities.readThumbnail(asset)), readArtifact: (id) => run(capabilities.readArtifact(id)),
            removeArtifact: (id) => run(capabilities.removeArtifact(id).pipe(Effect.tap(() => Effect.sync(() => update((s) => s.export.artifact?.id === id ? { ...s, export: { ...s.export, artifact: null } } : s))))),
            generateAnimations, cancelGeneration,
            previewAnimation: (animationId) => startPreview({ _tag: "animation", animationId }),
            previewTimeline: () => startPreview({ _tag: "timeline" }),
            selectAssets: (ids) => run(attempt(() => { guardOpen(); const unique = [...new Set(ids)]; if (unique.some((id) => !project().assets.some((asset) => asset.id === id)))
                throw new ValidationError({ path: "edit.asset", message: "素材不存在。" }); update((s) => ({ ...s, selection: { ...s.selection, assetIds: unique, assetId: unique.at(-1) ?? null, activeRegion: "library" } })); })),
            setActiveRegion: (activeRegion) => run(attempt(() => { guardOpen(); update((s) => ({ ...s, selection: { ...s.selection, activeRegion } })); })),
            prepare: () => {
                if (generationBusy())
                    return Promise.reject(workerError("busy", "请等待生成完成，或先取消生成。"));
                if (state.export.locked)
                    return Promise.reject(workerError("busy", "导出期间无法准备其他预览，请先取消导出。"));
                if (disposed)
                    return Promise.reject(workerError("unavailable", "编辑会话已关闭。"));
                policies.stopPrepare();
                manualPrepare?.interruptUnsafe();
                const sequence = ++prepareSequence;
                manualPrepare = start(prepare(generation, sequence), projectTasks);
                return observe(manualPrepare, generation);
            },
            seek: (timeMs, target) => run(seek(timeMs, target)), setPlayhead: (timeMs) => run(Effect.gen(function* () {
                yield* attempt(() => { guardOpen(); const content = project(); if (!Number.isFinite(timeMs) || timeMs < 0 || timeMs > content.durationMs)
                    throw new ValidationError({ path: "preview.time", message: "播放头须位于工程时间范围内。" }); if (state.preview.target._tag !== "timeline") {
                    ++seekSequence;
                    update((s) => ({ ...s, prepare: { ...idleJob(), plan: null }, preview: { ...s.preview, target: { _tag: "timeline" }, timelineTimeMs: timeMs, requestedTimeMs: timeMs, playing: false } }));
                    schedule(false);
                } });
                return yield* seek(timeMs);
            })),
            setPlaying: (playing) => run(attempt(() => {
                guardOpen();
                if (playing) {
                    guardEditable();
                    const content = project();
                    if (generationBusy())
                        throw workerError("busy", "请等待生成完成，或先取消生成。");
                    const ids = state.preview.target._tag === "timeline" ? inUse(content) : [state.preview.target.animationId];
                    if (missingFor(content, ids).length)
                        throw new ValidationError({ path: "animation.missing", message: "当前预览仍有未生成的动画，请先生成。" });
                    if (!planIsCurrent(state.prepare.plan))
                        throw workerError("protocol", "请等待当前预览准备完成。");
                }
                update((s) => ({ ...s, preview: { ...s.preview, playing } }));
            })),
            setZoom: (zoom) => run(attempt(() => { guardOpen(); if (!Number.isFinite(zoom) || zoom < 0.5 || zoom > 240)
                throw new ValidationError({ path: "timeline.zoom", message: "缩放须在 0.5–240 像素/秒之间。" }); update((s) => ({ ...s, zoom })); })),
            selectAsset: (id) => run(attempt(() => { guardOpen(); if (id !== null && !project().assets.some((asset) => asset.id === id))
                throw new ValidationError({ path: "edit.asset", message: "素材不存在。" }); update((s) => ({ ...s, selection: { ...s.selection, assetId: id, assetIds: id === null ? [] : [id], activeRegion: "library" } })); })),
            selectClip: (id) => run(Effect.gen(function* () {
                const scene = yield* attempt(() => { guardOpen(); const found = id === null ? null : annotationScenes(project()).find((scene) => scene.id === id); if (found === undefined)
                    throw new ValidationError({ path: "edit.scene", message: "镜头不存在。" }); update((s) => ({ ...s, selection: { ...s.selection, clipId: id, activeRegion: "timeline", assetIds: found ? [found.assetId] : s.selection.assetIds, assetId: found?.assetId ?? s.selection.assetId } })); return found; });
                if (scene)
                    yield* Effect.tryPromise({ try: () => commands.setPlayhead(scene.startMs), catch: asRenderError });
            })),
            selectCue: (id, input) => run(Effect.gen(function* () {
                const selection = yield* attempt(() => { guardOpen(); const selected = selectCueRange(project(), state.selection, id, input?.extend); update((s) => ({ ...s, selection: { ...s.selection, activeRegion: "subtitles", cueIds: selected.cueIds, anchorCueId: selected.anchorCueId } })); return selected; });
                yield* Effect.tryPromise({ try: () => commands.setPlayhead(selection.timeMs), catch: asRenderError });
            })),
            selectCues: (ids) => run(Effect.gen(function* () {
                const range = yield* attempt(() => { guardOpen(); const range = ids.length ? subtitleSelectionRange(project(), ids) : null; update((s) => ({ ...s, selection: { ...s.selection, activeRegion: "subtitles", cueIds: [...ids], anchorCueId: ids[0] ?? null } })); return range; });
                if (range)
                    yield* Effect.tryPromise({ try: () => commands.setPlayhead(range.startMs), catch: asRenderError });
            })),
            setExportSettings: (input) => run(attempt(() => { guardEditable(); const settings = checkedSettings(input); update((s) => ({ ...s, settings, error: null })); })),
            export: () => {
                const epoch = generation;
                let sequence = 0;
                return run(Effect.gen(function* () {
                    const input = yield* attempt(() => {
                        guardEditable();
                        if (generationBusy())
                            throw workerError("busy", "请等待生成完成，或先取消生成。");
                        const content = project();
                        const missing = missingFor(content, inUse(content));
                        if (missing.length)
                            throw new ValidationError({ path: "animation.missing", message: `请先生成动画：${missing.map((id) => content.assets.find((asset) => asset.id === content.animations.find((animation) => animation.id === id)?.assetId)?.name ?? id).join("、")}` });
                        const isolated = state.preview.target._tag === "animation";
                        const plan = state.prepare.plan;
                        if (!isolated && !planIsCurrent(plan))
                            throw workerError("protocol", "请等待当前修订的预览准备完成。");
                        sequence = ++exportSequence;
                        activeExport = { generation: epoch, revision: content.revision, sequence };
                        update((s) => ({ ...s, export: { ...idleJob(), status: "running", artifact: null, locked: true }, preview: { ...s.preview, playing: false }, error: null }));
                        return { content, plan, isolated, settings: state.settings };
                    });
                    const operation = Effect.gen(function* () {
                        let plan = input.plan;
                        if (input.isolated) {
                            policies.stopPrepare();
                            manualPrepare?.interruptUnsafe();
                            const preparation = ++prepareSequence;
                            ++seekSequence;
                            update((s) => ({ ...s, prepare: { ...idleJob(), plan: null }, preview: { ...s.preview, target: { _tag: "timeline" }, requestedTimeMs: s.preview.timelineTimeMs, updating: true } }));
                            const composing = { exportSequence: sequence, prepareSequence: preparation, fiber: start(prepare(epoch, preparation, true), projectTasks) };
                            exportPreparation = composing;
                            plan = yield* Fiber.join(composing.fiber).pipe(Effect.ensuring(Effect.sync(() => {
                                if (exportPreparation === composing)
                                    exportPreparation = null;
                            })));
                        }
                        yield* attempt(() => { guardEpoch(epoch); if (sequence !== exportSequence || !planIsCurrent(plan) || (plan.target?._tag ?? "timeline") !== "timeline")
                            throw cancelled(); });
                        return yield* capabilities.export(input.content, plan, input.settings);
                    });
                    return yield* operation.pipe(Effect.catch((error) => !current(epoch) || sequence !== exportSequence ? Effect.fail(cancelled()) : Effect.fail(error)), Effect.tap((artifact) => attempt(() => { guardEpoch(epoch); if (activeExport?.sequence !== sequence || sequence !== exportSequence)
                        throw cancelled(); if (artifact.projectRevision !== input.content.revision)
                        throw workerError("protocol", "导出成品与当前工程不一致。"); update((s) => ({ ...s, export: { ...s.export, status: "completed", artifact, jobId: artifact.jobId, error: null } })); })), Effect.tapError((error) => Effect.sync(() => { if (current(epoch) && activeExport?.sequence === sequence)
                        update((s) => ({ ...s, export: { ...s.export, status: error._tag === "Cancelled" ? "cancelled" : "failed", error: error._tag === "Cancelled" ? null : error } })); })), Effect.ensuring(Effect.sync(() => { if (current(epoch) && sequence === exportSequence && activeExport?.sequence === sequence) {
                        activeExport = null;
                        update((s) => ({ ...s, export: { ...s.export, locked: false } }));
                    } })));
                }), epoch);
            },
            cancel: (target = "all") => {
                if (target === "generate")
                    return cancelGeneration();
                if (target === "all" && generationBusy())
                    return cancelGeneration().then(() => commands.cancel("all"));
                // Only the preparation owned by this export is interrupted. An unrelated
                // explicit preview must survive cancel("export") when no export owns it.
                const composing = target !== "prepare" && exportPreparation?.exportSequence === activeExport?.sequence ? exportPreparation : null;
                if (composing && composing.prepareSequence === prepareSequence) {
                    ++prepareSequence;
                    activePrepare = null;
                    ++seekSequence;
                    update((s) => ({ ...s, prepare: { ...s.prepare, status: "cancelling", plan: null }, preview: { ...s.preview, updating: false, seeking: false, playing: false } }));
                }
                if (target !== "export") {
                    policies.stopPrepare();
                    manualPrepare?.interruptUnsafe();
                    ++prepareSequence;
                    activePrepare = null;
                    ++seekSequence;
                    update((s) => ({ ...s, prepare: { ...s.prepare, status: "cancelling", plan: null }, preview: { ...s.preview, playing: false, updating: false, seeking: false } }));
                }
                if (target !== "prepare") {
                    ++exportSequence;
                    if (state.export.locked)
                        update((s) => ({ ...s, export: { ...s.export, status: "cancelling" } }));
                }
                const epoch = generation;
                const cancelledPrepare = prepareSequence;
                const cancelledExport = exportSequence;
                const cleanup = target === "export" ? capabilities.cancel(target) : prepareLock.withPermit(capabilities.cancel(target));
                // Fiber.interrupt waits for prepare's remote cancellation finalizer and
                // permit release, so a completed cancellation admits the next generation.
                const cancellation = composing ? Effect.andThen(Fiber.interrupt(composing.fiber), cleanup) : cleanup;
                return run(cancellation.pipe(Effect.tap(() => Effect.sync(() => {
                    if (!current(epoch))
                        return;
                    if (target !== "prepare" && exportSequence === cancelledExport)
                        activeExport = null;
                    update((s) => ({ ...s, ...((target !== "export" || composing !== null) && prepareSequence === cancelledPrepare ? { prepare: { ...s.prepare, status: "cancelled", plan: null } } : {}), ...(target !== "prepare" && exportSequence === cancelledExport ? { export: { ...s.export, status: s.export.locked ? "cancelled" : s.export.status, locked: false } } : {}) }));
                }))), epoch);
            },
            clearError: () => update((s) => ({ ...s, error: null })),
        };
        const dispose = () => {
            if (disposal)
                return disposal;
            disposed = true;
            ++generation;
            ++prepareSequence;
            ++seekSequence;
            policies.stop();
            activePrepare = null;
            activeExport = null;
            state = freezeMetadata({ ...state, disposed: true, preview: { ...state.preview, playing: false, seeking: false, updating: false } });
            for (const listener of listeners) {
                try {
                    listener();
                }
                catch { /* Dispose must complete. */ }
            }
            unsubscribe();
            listeners.clear();
            for (const release of presenters)
                release();
            presenters.clear();
            disposal = Effect.runPromiseWith(context)(Effect.gen(function* () { yield* Scope.close(tasks, Exit.void); yield* capabilities.cancel("all"); }));
            return disposal;
        };
        yield* Scope.addFinalizer(owner, Effect.promise(dispose).pipe(Effect.ignore));
        return { get snapshot() { return snapshot(); }, getSnapshot: snapshot,
            subscribe: (listener) => { if (disposed)
                return () => { }; listeners.add(listener); return () => { listeners.delete(listener); }; },
            commands, presentFrames: (present) => {
                if (disposed)
                    return () => { };
                const unsubscribe = capabilities.presentFrames((native, frame) => { if (acceptsFrame(frame))
                    present(native, frame); });
                const release = () => { unsubscribe(); presenters.delete(release); };
                presenters.add(release);
                return release;
            }, dispose };
    });
}
